import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Phone, Mail, MapPin, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { SectionHead, Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BRAND } from "@/lib/scroll";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = ["Startup (1-50)", "SMB (50-500)", "Mid-Enterprise (500-5000)", "Enterprise (5000+)"];
const SERVICE_OPTIONS = [
  "Cloud Management & Cost Optimization",
  "Cloud Migration",
  "AWS Solutions",
  "Microsoft Azure Solutions",
  "Google Cloud (GCP)",
  "AI & Business Automation",
  "Custom Software & Product Development",
  "Application Modernization",
  "Microservices & Infrastructure Automation",
  "Other",
];

const EMPTY = { full_name: "", phone: "", email: "", company: "", company_size: "", services: [], message: "", agreed: false, website: "" };

const validate = (f) => {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name";
  if (!/^[+0-9][0-9\s-]{6,}$/.test(f.phone.trim())) e.phone = "Enter a valid phone number";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Enter a valid email address";
  if (f.company.trim().length < 2) e.company = "Please enter your company name";
  if (!f.company_size) e.company_size = "Select your company size";
  if (f.message.trim().length < 10) e.message = "Tell us a little more (min 10 characters)";
  if (!f.agreed) e.agreed = "Please agree to the Privacy Policy";
  return e;
};

const Err = ({ id, msg }) =>
  msg ? (
    <p data-testid={`${id}-error`} className="mt-1.5 text-xs font-medium text-red-500">{msg}</p>
  ) : null;

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const handler = (e) =>
      setForm((f) => (f.message ? f : { ...f, message: e.detail }));
    window.addEventListener("nexorbit:prefill-message", handler);
    return () => window.removeEventListener("nexorbit:prefill-message", handler);
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const toggleService = (s) =>
    set("services", form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Request received — our team will reach out within one business day.");
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "h-12 rounded-xl border-nexblue/15 bg-white text-nexink placeholder:text-nexink/40 focus-visible:ring-nexcyan";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32 bg-nexlight">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <SectionHead
          chapter="07"
          overline="Get In Touch"
          title="Ready to Transform Your Cloud Infrastructure?"
          sub="Let's discuss how NexOrbit Solutions can accelerate your cloud journey."
          testId="contact-heading"
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col justify-between rounded-2xl bg-[#072B5B] p-6 sm:p-10 text-white relative overflow-hidden">
              <div className="orbit-ring w-[360px] h-[360px] -right-32 -bottom-32" style={{ borderColor: "rgba(0,168,232,0.18)" }} aria-hidden="true" />
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-nexcyan">Talk To An Architect</p>
                <p className="mt-5 font-display text-2xl font-semibold leading-snug">
                  Every engagement starts with a free, no-pressure discovery call.
                </p>
                <ul className="mt-10 space-y-6">
                  {[
                    { icon: Phone, label: "Call us", value: BRAND.phone, testId: "contact-phone" },
                    { icon: Mail, label: "Email", value: BRAND.email, testId: "contact-email" },
                    { icon: MapPin, label: "Office", value: BRAND.address, testId: "contact-address" },
                  ].map(({ icon: Icon, label, value, testId }) => (
                    <li key={label} className="flex items-center gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-nexcyan">
                        <Icon size={18} />
                      </span>
                      <span>
                        <span className="block text-xs uppercase tracking-[0.2em] text-white/50">{label}</span>
                        <span className="block text-sm font-semibold break-words" data-testid={testId}>{value}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="relative mt-10 text-xs tracking-[0.3em] text-white/40 font-semibold">
                INNOVATE&nbsp;|&nbsp;EMPOWER&nbsp;|&nbsp;ELEVATE
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <div className="rounded-2xl border border-nexblue/10 bg-white p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              {sent && (
                <div
                  data-testid="contact-success-message"
                  className="mb-8 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
                >
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                  <span>Thank you — your consultation request has been received. A confirmation email is on its way, and our team will contact you within one business day.</span>
                </div>
              )}
              <form onSubmit={submit} noValidate data-testid="contact-form">
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={(e) => set("website", e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="full_name" className="text-nexink">Full Name *</Label>
                    <Input id="full_name" data-testid="contact-name-input" className={`${inputCls} mt-1.5`} placeholder="Jane Sharma"
                      value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
                    <Err id="contact-name" msg={errors.full_name} />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-nexink">Phone Number *</Label>
                    <Input id="phone" type="tel" data-testid="contact-phone-input" className={`${inputCls} mt-1.5`} placeholder="+91 98765 43210"
                      value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                    <Err id="contact-phone" msg={errors.phone} />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-nexink">Email Address *</Label>
                    <Input id="email" type="email" data-testid="contact-email-input" className={`${inputCls} mt-1.5`} placeholder="jane@company.com"
                      value={form.email} onChange={(e) => set("email", e.target.value)} />
                    <Err id="contact-email" msg={errors.email} />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-nexink">Company Name *</Label>
                    <Input id="company" data-testid="contact-company-input" className={`${inputCls} mt-1.5`} placeholder="Acme Technologies"
                      value={form.company} onChange={(e) => set("company", e.target.value)} />
                    <Err id="contact-company" msg={errors.company} />
                  </div>
                </div>

                <div className="mt-5">
                  <Label className="text-nexink">Company Size *</Label>
                  <Select value={form.company_size} onValueChange={(v) => set("company_size", v)}>
                    <SelectTrigger data-testid="contact-size-select" className={`${inputCls} mt-1.5`}>
                      <SelectValue placeholder="Select company size" />
                    </SelectTrigger>
                    <SelectContent>
                      {SIZES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`contact-size-option-${s.split(" ")[0].toLowerCase()}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Err id="contact-size" msg={errors.company_size} />
                </div>

                <fieldset className="mt-6">
                  <legend className="text-sm font-medium text-nexink">Service Interest</legend>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {SERVICE_OPTIONS.map((s, i) => (
                      <label key={s} className="flex cursor-pointer items-center gap-3 rounded-xl border border-nexblue/10 px-4 py-3 transition-colors duration-200 hover:border-nexcyan/50 has-[:checked]:border-nexcyan has-[:checked]:bg-nexcyan/5">
                        <Checkbox
                          data-testid={`contact-service-checkbox-${i + 1}`}
                          checked={form.services.includes(s)}
                          onCheckedChange={() => toggleService(s)}
                          className="border-nexblue/30 data-[state=checked]:bg-nexcyan data-[state=checked]:border-nexcyan"
                        />
                        <span className="text-sm text-nexink/80">{s}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="mt-6">
                  <Label htmlFor="message" className="text-nexink">Your Ask *</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    rows={4}
                    className="mt-1.5 rounded-xl border-nexblue/15 bg-white text-nexink placeholder:text-nexink/40 focus-visible:ring-nexcyan"
                    placeholder="Tell us about your business challenge, what you want to achieve, and any relevant timeline."
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                  />
                  <Err id="contact-message" msg={errors.message} />
                </div>

                <div className="mt-6">
                  <label className="flex cursor-pointer items-start gap-3">
                    <Checkbox
                      data-testid="contact-privacy-checkbox"
                      checked={form.agreed}
                      onCheckedChange={(v) => set("agreed", !!v)}
                      className="mt-0.5 border-nexblue/30 data-[state=checked]:bg-nexcyan data-[state=checked]:border-nexcyan"
                    />
                    <span className="text-sm text-nexink/70">I agree to the Privacy Policy</span>
                  </label>
                  <Err id="contact-privacy" msg={errors.agreed} />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-btn"
                  className="group mt-8 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-nexorange px-9 py-4 text-sm font-bold text-nexblue transition-transform duration-300 hover:scale-[1.03] hover:shadow-[0_12px_32px_rgba(255,149,0,0.35)] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
                  {sending ? "Sending..." : "Request a Consultation"}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
